import React, { useMemo } from 'react';
import { NavigateFunction } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { sp } from '@/theme/mui';

import LoadingWebinarContent from './LoadingWebinarContent';
import { LoadingTransition } from '@/components/atoms/LoadingTransition/LoadingTransition';
import { Skeleton } from '@/components/atoms/Skeleton/Skeleton';
import { Button } from '@/components/atoms/Button/Button';
import { CallToActionWebinar } from '@/components/atoms/CallToActioniWebinar/CallToActionWebinar';
import { DateIcon } from '@/components/atoms/DateIcon/DateIcon';
import { SanityContent } from '@/components/organisms/SanityContent/SanityContent';
import { Webinar as WebinarType } from '@karehero/models';
import { buildImageUrl } from '@/utils/sanity';

export const WebinarContent = ({
  content,
  isLoading = true,
  navigate,
  backPath,
}: WebinarContentProps) => {
  const headerImageUrl = useMemo(
    () => buildImageUrl(content?.headerImageUrl, 800),
    [content],
  );
  const callToActionImageUrl = useMemo(
    () => buildImageUrl(content?.callToActionImageUrl, 800),
    [content],
  );

  const duration = useMemo(() => {
    const startHour = parseInt(content?.startTime.split(':')[0] || '');
    const startMinutes = parseInt(content?.startTime.split(':')[1] || '');
    const endHour = parseInt(content?.endTime.split(':')[0] || '');
    const endMinutes = parseInt(content?.endTime.split(':')[1] || '');
    let markup = '';
    if (endHour - startHour > 0) markup += `${endHour - startHour} hours`;
    if (endHour - startHour > 0 && endMinutes - startMinutes > 0)
      markup += ' and ';
    if (endMinutes - startMinutes > 0)
      markup += `${endMinutes - startMinutes} minutes`;
    return markup;
  }, [content]);

  return (
    <Stack sx={{ gap: sp[12] }}>
      <div>
        <Button
          ariaLabel='Back'
          variant='link'
          iconLeft='chevron-left'
          onPress={() => navigate?.(backPath || '')}
          isInline
        >
          Back
        </Button>
      </div>
      <Stack sx={{ gap: sp[48] }}>
        <LoadingTransition
          isLoading={isLoading}
          loading={
            <Skeleton shape='header-image' isRounded={false} isMargin={false} />
          }
          content={
            <Box sx={{ position: 'relative' }}>
              <Box
                sx={{
                  borderRadius: sp[8],
                  overflow: 'hidden',
                  height: '300px',
                  display: 'flex',
                  alignItems: 'center',
                  '& > img': {
                    width: '100%',
                    objectFit: 'cover',
                    userSelect: 'none',
                    WebkitUserDrag: 'none',
                  },
                }}
              >
                <img
                  src={headerImageUrl}
                  alt={content?.title}
                  onError={({ currentTarget }) => {
                    currentTarget.onerror = null;
                    currentTarget.src =
                      'https://cdn.sanity.io/images/x7r1t5o8/production/e8579e2a4c3766cf0cf99a24a9d0679d2d2bbeb4-5322x2365.jpg?h=800';
                  }}
                />
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  top: '245px',
                  left: '12px',
                  width: '80px',
                  height: '80px',
                }}
              >
                <DateIcon date={new Date(content?.date || '')} size='md' />
              </Box>
            </Box>
          }
        />
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: sp[64] }}>
          <LoadingTransition
            isLoading={isLoading}
            loading={<LoadingWebinarContent />}
            content={
              <Stack sx={{ width: '100%' }}>
                <Box
                  sx={{
                    fontFamily: 'Lora, serif',
                    fontWeight: 400,
                    fontSize: '3rem',
                    lineHeight: 1.1,
                    color: '#003413',
                    mb: sp[32],
                  }}
                >
                  {content?.title}
                </Box>
                <Box
                  sx={{
                    typography: 'bodyXsMedium',
                    color: '#404042',
                    mb: sp[4],
                  }}
                >
                  Speaker: {content?.speaker}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: sp[8],
                    mb: sp[12],
                    typography: 'bodyXs',
                    color: '#8c8c8d',
                  }}
                >
                  <Box sx={{ typography: 'bodySmSemibold' }}>Duration:</Box>{' '}
                  {duration}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: sp[32],
                    py: sp[16],
                    mb: sp[32],
                    borderTop: '1px solid #cbc5b9',
                    borderBottom: '1px solid #cbc5b9',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: sp[8],
                      typography: 'bodySm',
                    }}
                  >
                    <Box sx={{ typography: 'bodySmSemibold' }}>Date:</Box>
                    {new Date(content?.date || '').toDateString()}
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: sp[8],
                      typography: 'bodySm',
                    }}
                  >
                    <Box sx={{ typography: 'bodySmSemibold' }}>Time: </Box>{' '}
                    {content?.startTime} - {content?.endTime}
                  </Box>
                </Box>
                <SanityContent content={content?.content} />
              </Stack>
            }
          />
          <CallToActionWebinar
            isLoading={isLoading}
            date={new Date(content?.date || '')}
            time={content?.startTime || ''}
            headerImageUrl={callToActionImageUrl}
          />
        </Box>
      </Stack>
    </Stack>
  );
};

interface WebinarContentProps {
  content?: WebinarType;
  isLoading?: boolean;
  navigate?: NavigateFunction;
  backPath?: string;
}

WebinarContent.displayName = 'WebinarContent';
export default WebinarContent;
